/**
 * Purpose: Custom hook for mapping and filtering reservations data for display.
 * 
 * High-level Workflow:
 * 1. Maps raw API reservations to UI format using shared mapper utility
 * 2. Adds computed status bucket fields (status_bucket, is_booked, is_inquiry, is_cancelled)
 * 3. Filters reservations by reporting period date overlap (check_in < rangeEnd AND check_out > rangeStart)
 * 4. Filters out inquiry and cancelled reservations from table display (only shown in KPIs)
 * 5. Returns filtered reservations for table display and KPI calculations
 * 
 * Assumptions & Invariants:
 * - reservations array comes from useReservationsData hook (raw API response items)
 * - rangeStart and rangeEnd are Date objects from ReportingPeriodContext
 * - Date overlap logic: reservation overlaps if check_in_date < rangeEnd AND check_out_date > rangeStart
 * - Table shows only BOOKED and OWNER_STAY reservations (inquiry and cancelled excluded)
 * - All filtering uses parseLocalDate to avoid timezone shifts for date-only strings
 * - Memoization boundaries preserve exact dependency semantics from original implementation
 * 
 * Dependencies:
 * Internal:
 *   - utils/ownerReservationsMapper.js - mapReservationsToUI function
 *   - utils/hostawayStatus.js - Status bucket utilities (getStatusBucket, isBookedStatus, isInquiryStatus, isCancelledStatus)
 *   - utils/parseLocalDate.js - Date parsing utility
 * External:
 *   - react - React hooks (useMemo)
 * 
 * Version: 1.0.0
 * Created: 2026-01-26
 * Change Log:
 * - 2026-01-26: Initial extraction from ReservationsPage.jsx (Phase 3)
 */

import { useMemo } from 'react';
import {
  getStatusBucket,
  isBookedStatus,
  isInquiryStatus,
  isCancelledStatus,
} from '../utils/hostawayStatus.js';
import { mapReservationsToUI } from '../utils/ownerReservationsMapper.js';
import { parseLocalDate } from '../utils/parseLocalDate.js';

/**
 * Custom hook for mapping and filtering reservations
 * 
 * Maps raw reservations to UI format, adds status bucket fields, and filters by date overlap and status.
 * Preserves exact memoization boundaries and dependencies from original implementation.
 * 
 * @param {Array} reservations - Raw reservations array from API
 * @param {Date|null} rangeStart - Start date from ReportingPeriodContext
 * @param {Date|null} rangeEnd - End date from ReportingPeriodContext
 * @param {boolean} debug - Debug flag for logging (optional, defaults to false)
 * @returns {Object} - { allMappedReservations, dateFilteredReservations, displayedReservations }
 */
export function useReservationsFiltering(reservations, rangeStart, rangeEnd, debug = false) {
  // Map API reservations to UI format using shared mapper + add status bucket fields
  // Phase 1.5: Memoized to prevent unnecessary recalculations
  const allMappedReservations = useMemo(() => {
    const mappedReservations = mapReservationsToUI(reservations);
    
    // Add computed status bucket fields (for filtering)
    return mappedReservations.map(r => ({
      ...r,
      // Add status bucket fields
      status_bucket: getStatusBucket(r.status),
      is_booked: isBookedStatus(r.status),
      is_inquiry: isInquiryStatus(r.status),
      is_cancelled: isCancelledStatus(r.status),
    }));
  }, [reservations]);

  // Filter by Reporting Period (API already filters, but ensure overlap logic)
  // Overlap rule: reservation overlaps if check_in_date < rangeEnd AND check_out_date > rangeStart
  const dateFilteredReservations = useMemo(() => {
    if (!rangeStart || !rangeEnd) return allMappedReservations;
    
    const rangeStartDate = new Date(rangeStart);
    const rangeEndDate = new Date(rangeEnd);
    rangeStartDate.setHours(0, 0, 0, 0);
    rangeEndDate.setHours(23, 59, 59, 999);
    
    const filtered = allMappedReservations.filter(r => {
      if (!r.check_in_date || !r.check_out_date) return false;
      
      // Use parseLocalDate to avoid timezone shifts for date-only strings
      const checkIn = parseLocalDate(r.check_in_date);
      const checkOut = parseLocalDate(r.check_out_date);
      
      if (!checkIn || !checkOut) return false;
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(23, 59, 59, 999);
      
      // Overlap: reservation overlaps if check_in < rangeEnd AND check_out > rangeStart
      return checkIn < rangeEndDate && checkOut > rangeStartDate;
    });
    
    if (debug) {
      console.log('[OWNER_FILTER] [useReservationsFiltering] Date filtering:', {
        allReservationsCount: allMappedReservations.length,
        dateFilteredCount: filtered.length,
        reportingPeriod: {
          start: rangeStart.toISOString().split('T')[0],
          end: rangeEnd.toISOString().split('T')[0],
        },
      });
    }
    
    return filtered;
  }, [allMappedReservations, rangeStart, rangeEnd, debug]);

  // CRITICAL: Exclude inquiry and cancelled from table (only show in KPIs)
  // Table shows only BOOKED and OWNER_STAY reservations
  const displayedReservations = useMemo(() => {
    const filtered = dateFilteredReservations.filter(r => {
      // Exclude inquiry and cancelled from table
      return !r.is_inquiry && !r.is_cancelled;
    });
    
    if (debug) {
      console.log('[OWNER_FILTER] [useReservationsFiltering] Table filtering (exclude inquiry/cancelled):', {
        dateFilteredCount: dateFilteredReservations.length,
        displayedCount: filtered.length,
        breakdown: {
          booked: dateFilteredReservations.filter(r => r.is_booked).length,
          ownerStay: dateFilteredReservations.filter(r => r.status_bucket === 'OWNER_STAY').length,
          cancelled: dateFilteredReservations.filter(r => r.is_cancelled).length,
          inquiry: dateFilteredReservations.filter(r => r.is_inquiry).length,
        },
      });
    }
    
    return filtered;
  }, [dateFilteredReservations, debug]);

  return {
    allMappedReservations,
    dateFilteredReservations,
    displayedReservations,
  };
}
